import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSave, FaArrowLeft, FaMapMarkerAlt } from 'react-icons/fa';
import axios from 'axios';

const StationEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [station, setStation] = useState(null);
  const [formData, setFormData] = useState({
    stationsnummer: '',
    navn: '',
    adresse: '',
    stationsleder: '',
    telefonnummer: '',
    doerkode: ''
  });

  useEffect(() => {
    fetchStationData();
  }, [id]);

  const fetchStationData = async () => {
    try {
      const response = await axios.get(`/api/stationer/${id}`);
      const stationData = response.data;
      setStation(stationData);
      setFormData({
        stationsnummer: stationData.stationsnummer || '',
        navn: stationData.navn,
        adresse: stationData.adresse,
        stationsleder: stationData.stationsleder || '',
        telefonnummer: stationData.telefonnummer || '',
        doerkode: stationData.doerkode || ''
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching station data:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`/api/stationer/${id}`, formData);
      navigate(`/stationer/profil/${id}`);
    } catch (error) {
      console.error('Error updating station:', error);
      alert('Der opstod en fejl under opdatering af stationen');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  if (!station) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Station ikke fundet</h2>
          <button
            onClick={() => navigate('/stationer')}
            className="mt-4 px-4 py-2 bg-falck-blue text-white rounded-lg hover:bg-blue-700"
          >
            Tilbage til stationer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/stationer/profil/${id}`)}
            className="p-2 text-gray-600 hover:text-gray-800"
          >
            <FaArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Rediger station: {station.navn}
            </h1>
            <p className="text-gray-600">Opdater station information</p>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Grundlæggende Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <FaMapMarkerAlt className="text-falck-blue" />
                Grundlæggende Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stationsnummer *
                  </label>
                  <input
                    type="text"
                    name="stationsnummer"
                    required
                    value={formData.stationsnummer}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Navn *
                  </label>
                  <input
                    type="text"
                    name="navn"
                    required
                    value={formData.navn}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse *
                  </label>
                  <input
                    type="text"
                    name="adresse"
                    required
                    value={formData.adresse}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stationsleder
                  </label>
                  <input
                    type="text"
                    name="stationsleder"
                    value={formData.stationsleder}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Kontakt Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Kontakt Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefonnummer
                  </label>
                  <input
                    type="text"
                    name="telefonnummer"
                    value={formData.telefonnummer}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dørkode
                  </label>
                  <input
                    type="text"
                    name="doerkode"
                    value={formData.doerkode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/stationer/profil/${id}`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Annuller
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center gap-2 transition-colors ${
                saving 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-falck-blue hover:bg-blue-700'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Gemmer...
                </>
              ) : (
                <>
                  <FaSave />
                  Gem ændringer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StationEdit;
